const clean = (value) => String(value ?? '').trim();

const getResponseMessage = (error) =>
  clean(
    error?.response?.data?.code ||
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.code ||
      error?.message
  );

/**
 * REST MediaSoup failures need different recovery paths. In particular, a
 * producer 404 is a harmless stale announcement while a transport 404 means
 * the receive transport must be rebuilt before consuming again.
 */
export const classifyMediaApiError = (error) => {
  const status = Number(error?.response?.status || 0);
  const message = getResponseMessage(error);
  const responseCode = clean(error?.response?.data?.code).toUpperCase();

  // The backend deliberately reuses HTTP 409/410 for several lifecycle
  // outcomes. The machine-readable code is authoritative; status alone cannot
  // tell a stale producer from an ended meeting.
  if (
    [
      'PRODUCER_GONE',
      'PRODUCER_REPLACED',
      'PRODUCER_NOT_FOUND',
      'STALE_PRODUCER',
    ].includes(responseCode)
  ) {
    return 'stale-producer';
  }
  if (
    [
      'RECV_TRANSPORT_GONE',
      'SEND_TRANSPORT_GONE',
      'TRANSPORT_GONE',
      'TRANSPORT_NOT_FOUND',
      'CONSUMER_GONE',
      'CONSUMER_NOT_FOUND',
      'STALE_TRANSPORT',
    ].includes(responseCode)
  ) {
    return 'stale-transport';
  }
  if (
    [
      'MEDIA_REJOIN_REQUIRED',
      'SESSION_STALE',
      'MEDIA_SESSION_STALE',
      'MEDIA_STATE_SESSION_STALE',
      // A REST request can race the exact-session Socket.IO rejoin after a
      // namespace reconnect. The socket join recreates/rebinds the participant
      // row, so this is recoverable and must not be treated as revoked access.
      'NOT_ADMITTED',
    ].includes(responseCode)
  ) {
    return 'rejoin';
  }
  if (responseCode === 'MEETING_ENDED') return 'meeting-ended';
  if (responseCode === 'MEETING_NOT_STARTED_YET') return 'not-started';
  if (
    [
      'ROOM_ACCESS_DENIED',
      'ROOM_NOT_FOUND',
      'NOT_AUTHORISED',
      'PERMANENTLY_REJECTED',
      'JOIN_REJECTED',
    ].includes(responseCode)
  ) {
    return 'access-denied';
  }

  if (
    status === 404 &&
    /producer|cannot consume your own producer/i.test(message)
  ) {
    return 'stale-producer';
  }
  if (
    status === 404 &&
    /transport|owned receive|owned send/i.test(message)
  ) {
    return 'stale-transport';
  }
  if (status === 409 && /producer|consume/i.test(message)) {
    return 'stale-producer';
  }
  if (status === 403) return 'authorization';
  if (status === 410 && /meeting.+ended|room.+ended/i.test(message)) {
    return 'meeting-ended';
  }
  if (status === 410 && /producer/i.test(message)) return 'stale-producer';
  if (status === 410 && /transport|consumer/i.test(message)) {
    return 'stale-transport';
  }
  if (status === 401) return 'authentication';
  if (
    error?.code === 'RECV_TRANSPORT_CHANGED' ||
    error?.code === 'RECV_TRANSPORT_REBUILD_REQUIRED'
  ) {
    return 'stale-transport';
  }
  if (
    error?.name === 'OperationError' ||
    /sessiondescription|duplicate a=msid|failed to parse/i.test(message)
  ) {
    return 'stale-transport';
  }
  return 'transient';
};

/**
 * Older deployments sometimes returned a bare HTTP 410 without the newer
 * machine-readable code. For a consume/resume request that means the receive
 * path is stale; rebuilding only that transport is safer than blanking every
 * tile or treating the whole meeting as ended. A coded MEETING_ENDED response
 * remains authoritative through classifyMediaApiError().
 */
export const classifyMediaOperationError = (
  error,
  operation = ''
) => {
  const classification = classifyMediaApiError(error);
  const status = Number(error?.response?.status || 0);
  const normalizedOperation = clean(operation).toLowerCase();
  if (
    classification === 'transient' &&
    status === 410 &&
    ['consume', 'resume-consumer'].includes(normalizedOperation)
  ) {
    return 'stale-transport';
  }
  return classification;
};

/**
 * Socket join acknowledgements use a machine-readable code alongside a
 * user-facing error string. Classifying the code prevents fatal access/session
 * failures from entering the automatic media retry loop.
 */
export const classifyMeetingJoinFailure = (value = {}) => {
  const code = clean(
    value?.joinResponse?.code ||
      value?.response?.data?.code ||
      value?.code ||
      value?.reason
  ).toUpperCase();

  if (!code) return 'transient';
  if (code === 'DUPLICATE_DEVICE_SESSION') return 'duplicate-session';
  if (['NOT_ADMITTED', 'MEDIA_REJOIN_REQUIRED'].includes(code)) {
    return 'rejoin';
  }
  if (
    [
      'UNAUTHENTICATED_SOCKET',
      'INVALID_SESSION',
      'USER_ID_MISMATCH',
      'MISSING_JOIN_DATA',
    ].includes(code)
  ) {
    return 'session-invalid';
  }
  if (code === 'MEETING_ENDED') return 'meeting-ended';
  if (code === 'MEETING_NOT_STARTED_YET') return 'not-started';
  if (
    [
      'ROOM_NOT_FOUND',
      'ROOM_ACCESS_DENIED',
      'NOT_AUTHORISED',
      'PERMANENTLY_REJECTED',
      'JOIN_REJECTED',
    ].includes(code)
  ) {
    return 'access-denied';
  }
  return 'transient';
};

/**
 * Decide whether the first page load may request camera/microphone permission.
 * Waiting participants keep their pre-join intent but receive an empty stream
 * until an authoritative socket acknowledgement admits them.
 */
export const resolveInitialMeetingPublishAccess = ({
  room = {},
  userId = '',
  sessionId = '',
} = {}) => {
  const cleanUserId = clean(userId);
  const cleanSessionId = clean(sessionId);
  const participants = Array.isArray(room?.participants)
    ? room.participants
    : [];
  const participant = participants.find((item = {}) => {
    const participantUserId = clean(item.user?._id || item.user || item.userId);
    const participantSessionId = clean(item.sessionId);
    return (
      participantUserId === cleanUserId &&
      (!cleanSessionId || participantSessionId === cleanSessionId)
    );
  });
  const hostId = clean(room?.host?._id || room?.host);
  const originalHostId = clean(room?.originalHost?._id || room?.originalHost);
  const isHost =
    room?.access?.isHost === true ||
    participant?.isHost === true ||
    (cleanUserId && [hostId, originalHostId].includes(cleanUserId));
  const status = clean(participant?.status).toLowerCase();

  return {
    mayPublish:
      isHost || ['admitted', 'presenting'].includes(status),
    status: status || (isHost ? 'admitted' : 'waiting'),
    participant: participant || null,
  };
};

export const resolveRemoteMediaIntent = ({
  socketValue,
  participantValue,
  hasLiveTrack = false,
} = {}) => {
  if (typeof socketValue === 'boolean') {
    return { known: true, enabled: socketValue, source: 'socket' };
  }
  if (typeof participantValue === 'boolean') {
    return { known: true, enabled: participantValue, source: 'room' };
  }
  if (hasLiveTrack) {
    return { known: true, enabled: true, source: 'track' };
  }
  return { known: false, enabled: false, source: 'unknown' };
};

export const shouldShowVideoReceiveFailure = ({
  isLocal = false,
  isScreenSharing = false,
  mediaStateKnown = false,
  mediaEnabled = false,
  status = '',
} = {}) =>
  !isLocal &&
  !isScreenSharing &&
  mediaStateKnown &&
  mediaEnabled &&
  ['stalled', 'unavailable'].includes(clean(status).toLowerCase());
